import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsCard, { Img } from "../components/NewsCard";
import AdSlot from "../components/AdSlot";
import BriefModal from "../components/BriefModal";
import ShareModal from "../components/ShareModal";
import DailyBriefModal from "../components/DailyBriefModal";
import SponsorModal from "../components/SponsorModal";
import RateBar from "../components/RateBar";
import CreditsWidget from "../components/CreditsWidget";
import { NewsezeAnalysis } from "../components/AnalysisAndComments";
import AskUsSection from "../components/AskUsSection";
import AcrossTheAisle from "../components/AcrossTheAisle";
import JsonLd from "../components/JsonLd";
import { SeoHead } from "../components/SeoHead";
import { ReadAloudButton } from "./ReaderTools";
import { useAuth } from "../auth/AuthContext";
import { fetchArticle, fetchNews } from "../lib/api";
import useNewsezeUser from "../lib/useNewsezeUser";
import { Sparkles, Share2, ExternalLink, ArrowLeft, Coins, Download } from "lucide-react";

export default function Article() {
  const { id } = useParams();
  const [art, setArt] = useState(null);
  const [related, setRelated] = useState([]);
  const [brief, setBrief] = useState(null);
  const [share, setShare] = useState(null);
  const [showBriefSub, setShowBriefSub] = useState(false);
  const [showSponsor, setShowSponsor] = useState(false);
  const [credited, setCredited] = useState(false);
  const { email, track } = useNewsezeUser();
  const { token, user } = useAuth();
  const isReaderOrUp = ["basic","lite","pro","channels","elite","business"].includes(user?.tier || "free");

  useEffect(() => {
    let alive = true;
    fetchArticle(id).then((a) => {
      if (!alive) return;
      setArt(a);
      fetchNews({ category: a.category, limit: 6 }).then((rows) => {
        if (alive) setRelated(rows.filter((r) => r.id !== a.id).slice(0, 4));
      });
    }).catch(console.error);
    return () => { alive = false; };
  }, [id]);

  // Award attention credit after 30 seconds on this article (subscriber only)
  useEffect(() => {
    if (!email || !art || credited) return;
    const start = Date.now();
    const timer = setTimeout(async () => {
      const r = await track("article", art.id, Math.round((Date.now() - start) / 1000));
      if (r?.awarded > 0) setCredited(true);
    }, 30000);
    return () => clearTimeout(timer);
  }, [email, art, credited, track]);

  if (!art) {
    return (
      <>
        <Header onOpenBrief={() => setShowBriefSub(true)} />
        <main className="max-w-[1280px] mx-auto px-5 py-10 text-center text-[var(--muted)]">Loading story…</main>
        <Footer onOpenBrief={() => setShowBriefSub(true)} onOpenSponsor={() => setShowSponsor(true)} />
      </>
    );
  }

  return (
    <>
      <SeoHead
        title={art.title}
        description={art.why_it_matters || art.summary || ""}
      />
      <JsonLd
        id="newseze-jsonld-article"
        data={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: art.title,
          datePublished: art.published,
          dateModified: art.published,
          image: art.image
            ? [art.image]
            : [`https://newseze.com/og-default.png`],
          author: {
            "@type": "Person",
            name: (art.avatar && art.avatar.name) || art.source || "Newseze Wire",
          },
          publisher: {
            "@type": "Organization",
            name: "Newseze",
            logo: {
              "@type": "ImageObject",
              url: "https://newseze.com/logo.png",
            },
          },
          description: (art.summary || "").slice(0, 400),
          articleSection: art.category || "News",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://newseze.com/article/${art.id}`,
          },
        }}
      />
      <Header onOpenBrief={() => setShowBriefSub(true)} />

      <main className="max-w-[1280px] mx-auto px-5 py-6">
        <div className="mb-4">
          <Link to={`/section/${art.category}`} className="btn icon" data-testid="back-link">
            <ArrowLeft size={12} /> Back to {art.category}
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <article>
            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--red)]">{art.category}</div>
            <h1 className="text-[34px] md:text-[42px] font-black leading-[1.05] tracking-[-0.025em] mt-2 text-[var(--ink)]" data-testid="article-title">
              {art.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-[13px] text-[var(--muted)]">
              <span className="font-black text-[var(--ink)]">Newseze Wire</span>
              <span>·</span>
              <span>{new Date(art.published).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              {art.source && (
                <span className="text-[11px] uppercase tracking-[0.14em] font-bold opacity-70 ml-1" data-testid="article-wire-source">
                  Wire: {art.source}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={() => setBrief(art)} className="btn primary" data-testid="article-brief-btn">
                <Sparkles size={14} /> Brief Me
              </button>
              <button onClick={() => setShare(art)} className="btn" data-testid="article-share-btn">
                <Share2 size={14} /> Share / Send
              </button>
              <a href={art.link} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer" className="btn" data-testid="article-source-link">
                <ExternalLink size={14} /> Open original source
              </a>
              <Link to={`/read/${art.id}`} className="btn primary" data-testid="article-inframe-link">
                <ExternalLink size={14} /> Read full story (in-site)
              </Link>
              <button
                onClick={() => {
                  document.body.classList.add("newseze-print-mode");
                  setTimeout(() => {
                    window.print();
                    document.body.classList.remove("newseze-print-mode");
                  }, 50);
                }}
                className="btn"
                data-testid="article-download-pdf-btn"
                title="Print or save this story as a PDF"
              >
                <Download size={14} /> Download / Print
              </button>
              <div className="inline-block min-w-[200px]">
                <ReadAloudButton articleId={art.id} token={token} isReaderOrUp={isReaderOrUp} />
              </div>
              <RateBar article={art} />
              {credited && (
                <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[var(--green)] px-2.5 py-1.5 rounded-md bg-[#e8f7f1] border border-[var(--green)]/30" data-testid="article-credited">
                  <Coins size={12} /> +10 credits earned
                </span>
              )}
            </div>

            <div className="mt-6 rounded-md overflow-hidden border border-[var(--line)]">
              <Img src={art.image} category={art.category} alt={art.title} headline={art.title} source={art.source} />
            </div>

            <div className="prose max-w-none mt-6 text-[16px] leading-[1.7] text-[var(--ink)]">
              <p>{art.summary}</p>
              <p className="text-[12px] text-[var(--muted)] mt-6 italic border-t border-[var(--line)] pt-3" data-testid="article-attribution-note">
                <strong>Sourcing &amp; attribution.</strong> Newseze provides AI-curated summaries, narrative
                framing, and editorial analysis. The underlying reporting was contributed by
                {art.source ? <> <strong>{art.source}</strong></> : <> a partner newsroom</>}; tap
                &ldquo;Open original source&rdquo; above to read their full reporting and support the
                contributing newsroom directly.
              </p>
            </div>

            <div id="newseze-analysis">
              <NewsezeAnalysis articleId={art.id} />
            </div>
            <AcrossTheAisle articleId={art.id} />
            <div id="ask-us-section">
              <AskUsSection articleId={art.id} />
            </div>

            <AdSlot size="banner" slot={`article-${art.category}-mid`} />

            <div className="mt-10">
              <div className="section-title">
                <span className="pip" />
                <h2>Related stories</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((a) => (
                  <NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />
                ))}
              </div>
            </div>
          </article>

          <aside className="space-y-5">
            <CreditsWidget onOpenBrief={() => setShowBriefSub(true)} />
            <div className="daily-brief">
              <div className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">My Daily Brief</div>
              <h3 className="text-[20px] font-black leading-tight mt-2">Honest news.<br />That rewards you for reading.</h3>
              <button onClick={() => setShowBriefSub(true)} className="btn gold mt-3 w-full justify-center" data-testid="article-rail-brief">Subscribe free</button>
            </div>
            <AdSlot size="h-rect" slot={`article-${art.category}-rect`} />
            <div className="sponsor-cta">
              <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Sponsor</div>
              <h4 className="text-[18px] font-black mt-1 leading-tight">Place your message here.</h4>
              <button onClick={() => setShowSponsor(true)} className="btn gold mt-3 w-full justify-center">Request media kit</button>
            </div>
            <AdSlot size="sky" slot={`article-${art.category}-sky`} />
          </aside>
        </div>
      </main>

      <Footer onOpenBrief={() => setShowBriefSub(true)} onOpenSponsor={() => setShowSponsor(true)} />

      {brief && <BriefModal article={brief} onClose={() => setBrief(null)} />}
      {share && <ShareModal article={share} onClose={() => setShare(null)} />}
      {showBriefSub && <DailyBriefModal onClose={() => setShowBriefSub(false)} />}
      {showSponsor && <SponsorModal onClose={() => setShowSponsor(false)} />}
    </>
  );
}
